import { v1 as uuidV1 } from 'uuid';

import { updateTabField, buildField, buildCertificateField, getFieldValueIfDirty } from '../../common/utils';
import { COMMON_FIELDS, PROTECTED_DOMAIN_FIELDS, IS_NEW_FIELD } from '../../common/fields';
import * as actions from './protectedDomainsActions';
import { commonActionsReducer } from '../../common/actions';
import {
    validateFilledForwarding,
    validateLdapServerUnique,
    validateAdsyncNodesRequiredAndUnique
} from '../../common/validators';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const {
    DOMAIN_ID,
    DOMAIN_NAME,
    DOMAIN_SHORT_NAME,
    LDAP_DOMAIN_NAME,
    LDAP_IP,
    USE_SECURE_LDAP,
    BACKUP_LDAP_IP,
    BACKUP_USE_SECURE_LDAP,
    DOMAIN_SERVICE_ACCOUNT,
    KERBEROS_FORWARDING,
    NTLM_FORWARDING,
    LDAP_FORWARDING,
    LDAPS_FORWARDING,
    API_CERTIFICATE_REQUEST,
    API_CERTIFICATE,
    ASSIGNED_NODES,
    ADSYNC_NODES,
    ROOT_CA_CERTIFICATE
} = PROTECTED_DOMAIN_FIELDS;

const { LDAP_IP_OPTIONS } = COMMON_FIELDS;

export const fieldsReducer = (state, action) => {
    const systemMode = action.payload.systemMode;
    let newState = state;
    switch (action.type) {
        case actions.UPDATE_DOMAIN:
            newState = updateTabField(state, action.payload.domainId, action.payload.fieldId, action.payload.value);
            if (action.payload.fieldId === DOMAIN_NAME.ID) {
                newState[action.payload.domainId].name = action.payload.value;
            }
            validateFilledForwarding(
                newState[action.payload.domainId],
                KERBEROS_FORWARDING.ID,
                NTLM_FORWARDING.ID,
                LDAP_FORWARDING.ID,
                LDAPS_FORWARDING.ID
            );
            validateLdapServerUnique(newState[action.payload.domainId], LDAP_IP.ID, BACKUP_LDAP_IP.ID);

            if (action.payload.fieldId === ADSYNC_NODES.ID) {
                validateAdsyncNodesRequiredAndUnique(newState);
            }
            return newState;
        case actions.ADD_DOMAIN:
            return addDomain(state, false, systemMode);
        case actions.DELETE_DOMAIN:
            return deleteDomain(state, action.payload.domainId);
        case actions.UPDATE_DOMAINS_FROM_FILE:
            return updateDomainsFromFile(
                state,
                action.payload.newDomains,
                action.payload.LdapServerOptions,
                false,
                systemMode
            );
        case actions.LDAP_IP_ADDED:
            let optionsFieldId = '';
            optionsFieldId = LDAP_IP_OPTIONS.ID;
            newState[optionsFieldId] = {
                ...state[optionsFieldId],
                value: state[optionsFieldId].value.concat(action.payload.value)
            };
            return newState;
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

export const initFieldsState = ({ settings }, wizard = false) => {
    const { DomainsConfigurations, NodesConfigurations } = settings;
    if (!DomainsConfigurations) {
        return {};
    }
    const domainFields = {};

    for (const domainId in DomainsConfigurations) {
        const assignedNodes = [];
        for (const nodeId in NodesConfigurations) {
            if (domainId in NodesConfigurations[nodeId].domainsConfiguration) {
                assignedNodes.push(nodeId);
            }
        }
        const domainObj = DomainsConfigurations[domainId];
        domainFields[domainId] = {
            id: domainId,
            name: domainObj.DomainName,
            fields: initDomainField(domainObj, assignedNodes, settings.LdapServerOptions, wizard),
            validators: {
                filledForwarding: [KERBEROS_FORWARDING.ID, NTLM_FORWARDING.ID, LDAP_FORWARDING.ID, LDAPS_FORWARDING.ID],
                ldapServerUnique: [LDAP_IP.ID, BACKUP_LDAP_IP.ID],
                adsyncNodesRequiredAndUnique: settings.systemMode === SYSTEM_MODES.ON_PREM,
                uniqueName: { fieldId: DOMAIN_NAME.ID, caseSensitive: true }
            }
        };
    }

    domainFields[LDAP_IP_OPTIONS.ID] = buildField(LDAP_IP_OPTIONS, settings.LdapServerOptions);

    return domainFields;
};

export const mapToSettings = (saveAll, selectedTab, fields, currentSettings) => {
    const newSettings = {
        DomainsConfigurations: { ...currentSettings.DomainsConfigurations }
    };
    if (saveAll) {
        for (const fieldId in fields) {
            if (fieldId === LDAP_IP_OPTIONS.ID) continue;
            const newDomainObj = mapDomainToSettings(
                currentSettings.DomainsConfigurations[fieldId],
                fields[fieldId].fields
            );
            newSettings.DomainsConfigurations[fieldId] = newDomainObj;
        }
    } else {
        const newDomainObj = mapDomainToSettings(
            currentSettings.DomainsConfigurations[selectedTab],
            fields[selectedTab].fields
        );
        newSettings.DomainsConfigurations[selectedTab] = newDomainObj;
    }

    newSettings.LdapServerOptions = fields[LDAP_IP_OPTIONS.ID].value;

    return newSettings;
};

export const mapDomainToSettings = (currentDomainObj, domainFields) => {
    const newDomainObj = { ...currentDomainObj };
    newDomainObj.id = domainFields[DOMAIN_ID.ID].value;
    newDomainObj.DomainName = getFieldValueIfDirty(domainFields[DOMAIN_NAME.ID]);
    newDomainObj.DomainShortName = getFieldValueIfDirty(domainFields[DOMAIN_SHORT_NAME.ID]);
    newDomainObj.LdapDomainName = getFieldValueIfDirty(domainFields[LDAP_DOMAIN_NAME.ID]);
    newDomainObj.LdapAuthUrl = getFieldValueIfDirty(
        domainFields[LDAP_IP.ID],
        domainFields[LDAP_IP.ID].value ? domainFields[LDAP_IP.ID].value : ''
    );
    newDomainObj.shouldSecureLDAP = getFieldValueIfDirty(domainFields[USE_SECURE_LDAP.ID]);
    newDomainObj.backupLdapAuthUrl = getFieldValueIfDirty(
        domainFields[BACKUP_LDAP_IP.ID],
        domainFields[BACKUP_LDAP_IP.ID].value ? domainFields[BACKUP_LDAP_IP.ID].value : ''
    );
    newDomainObj.shouldSecureBackupLDAP = getFieldValueIfDirty(domainFields[BACKUP_USE_SECURE_LDAP.ID]);
    newDomainObj.ADSyncDomainNodes = getFieldValueIfDirty(domainFields[ADSYNC_NODES.ID]);

    newDomainObj.ServiceAccountUsername = getFieldValueIfDirty(
        domainFields[DOMAIN_SERVICE_ACCOUNT.ID],
        domainFields[DOMAIN_SERVICE_ACCOUNT.ID].value.user
    );
    newDomainObj.ServiceAccountPassword = getFieldValueIfDirty(
        domainFields[DOMAIN_SERVICE_ACCOUNT.ID],
        domainFields[DOMAIN_SERVICE_ACCOUNT.ID].value.pass
    );

    newDomainObj.KerberosForwarding = getFieldValueIfDirty(domainFields[KERBEROS_FORWARDING.ID]);
    newDomainObj.NTLMForwarding = getFieldValueIfDirty(domainFields[NTLM_FORWARDING.ID]);
    newDomainObj.LdapForwarding = getFieldValueIfDirty(domainFields[LDAP_FORWARDING.ID]);
    newDomainObj.LdapsForwarding = getFieldValueIfDirty(domainFields[LDAPS_FORWARDING.ID]);

    newDomainObj.CertificateRequest = getFieldValueIfDirty(
        domainFields[API_CERTIFICATE_REQUEST.ID],
        domainFields[API_CERTIFICATE_REQUEST.ID].value.data
    );

    newDomainObj.CertificateData = getFieldValueIfDirty(domainFields[API_CERTIFICATE.ID]);
    newDomainObj.rootCACertificateData = getFieldValueIfDirty(domainFields[ROOT_CA_CERTIFICATE.ID]);
    return newDomainObj;
};

export const mapDomainToSettingsByWizard = (currentDomainObj, domainFields) => {
    const newDomainObj = { ...currentDomainObj };
    newDomainObj.id = domainFields[DOMAIN_ID.ID].value;
    newDomainObj.DomainName = domainFields[DOMAIN_NAME.ID].value;
    newDomainObj.DomainShortName = domainFields[DOMAIN_SHORT_NAME.ID].value;
    newDomainObj.LdapDomainName = domainFields[LDAP_DOMAIN_NAME.ID].value;
    newDomainObj.LdapAuthUrl = domainFields[LDAP_IP.ID].value ? domainFields[LDAP_IP.ID].value : '';
    newDomainObj.shouldSecureLDAP = domainFields[USE_SECURE_LDAP.ID].value;
    newDomainObj.backupLdapAuthUrl = domainFields[BACKUP_LDAP_IP.ID].value ? domainFields[BACKUP_LDAP_IP.ID].value : '';
    newDomainObj.shouldSecureBackupLDAP = domainFields[BACKUP_USE_SECURE_LDAP.ID].value;
    newDomainObj.ADSyncDomainNodes = currentDomainObj.ADSyncDomainNodes;

    newDomainObj.ServiceAccountUsername = currentDomainObj.ServiceAccountUsername;
    newDomainObj.ServiceAccountPassword = currentDomainObj.ServiceAccountPassword;

    newDomainObj.KerberosForwarding = domainFields[KERBEROS_FORWARDING.ID].value;
    newDomainObj.NTLMForwarding = domainFields[NTLM_FORWARDING.ID].value;
    newDomainObj.LdapForwarding = domainFields[LDAP_FORWARDING.ID].value;
    newDomainObj.LdapsForwarding = domainFields[LDAPS_FORWARDING.ID].value;

    newDomainObj.CertificateRequest = domainFields[API_CERTIFICATE_REQUEST.ID].value.data;

    newDomainObj.CertificateData = domainFields[API_CERTIFICATE.ID].value;
    newDomainObj.rootCACertificateData = domainFields[ROOT_CA_CERTIFICATE.ID].value;
    return newDomainObj;
};

export const initDomainField = (domainObj, assignedNodes, ldapServerOptions, wizard = false) => {
    const ldapIpSelectedOption = ldapServerOptions
        ? ldapServerOptions.find((option) => option.value === (domainObj.LdapAuthUrl || ''))
        : { value: '' };
    const backupLdapIpSelectedOption = ldapServerOptions
        ? ldapServerOptions.find((option) => option.value === (domainObj.backupLdapAuthUrl || ''))
        : { value: '' };
    const domainFields = {
        [DOMAIN_ID.ID]: buildField(DOMAIN_ID, domainObj.id),
        [DOMAIN_NAME.ID]: buildField(DOMAIN_NAME, domainObj.DomainName || '', { required: true, maxLength: 40 }),
        [DOMAIN_SHORT_NAME.ID]: buildField(DOMAIN_SHORT_NAME, domainObj.DomainShortName || '', { required: true }),
        [LDAP_DOMAIN_NAME.ID]: buildField(LDAP_DOMAIN_NAME, domainObj.LdapDomainName || '', {
            notContainWhitespace: true
        }),
        [LDAP_IP.ID]: {
            ...buildField(LDAP_IP, ldapIpSelectedOption ? ldapIpSelectedOption.value : '', { domainOrIp: true }),
            options: ldapServerOptions
        },
        [USE_SECURE_LDAP.ID]: buildField(
            USE_SECURE_LDAP,
            'shouldSecureLDAP' in domainObj ? domainObj.shouldSecureLDAP : false
        ),
        [BACKUP_LDAP_IP.ID]: {
            ...buildField(BACKUP_LDAP_IP, backupLdapIpSelectedOption ? backupLdapIpSelectedOption.value : '', {
                domainOrIp: true
            }),
            options: ldapServerOptions
        },
        [BACKUP_USE_SECURE_LDAP.ID]: buildField(
            BACKUP_USE_SECURE_LDAP,
            'shouldSecureBackupLDAP' in domainObj ? domainObj.shouldSecureBackupLDAP : false
        ),
        [KERBEROS_FORWARDING.ID]: buildField(
            KERBEROS_FORWARDING,
            'KerberosForwarding' in domainObj ? domainObj.KerberosForwarding : true
        ),
        [NTLM_FORWARDING.ID]: buildField(
            NTLM_FORWARDING,
            'NTLMForwarding' in domainObj ? domainObj.NTLMForwarding : true
        ),
        [LDAP_FORWARDING.ID]: buildField(
            LDAP_FORWARDING,
            'LdapForwarding' in domainObj ? domainObj.LdapForwarding : true
        ),
        [LDAPS_FORWARDING.ID]: buildField(
            LDAPS_FORWARDING,
            'LdapsForwarding' in domainObj ? domainObj.LdapsForwarding : true
        ),
        [API_CERTIFICATE_REQUEST.ID]: buildField(API_CERTIFICATE_REQUEST, {
            data: domainObj.CertificateRequest || '',
            fqdn: '',
            san: ''
        }),
        [API_CERTIFICATE.ID]: buildCertificateField(API_CERTIFICATE, domainObj.CertificateData || ''),
        [ROOT_CA_CERTIFICATE.ID]: buildCertificateField(ROOT_CA_CERTIFICATE, domainObj.rootCACertificateData || ''),
        [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, domainObj.isNew)
    };
    if (!wizard) {
        domainFields[ASSIGNED_NODES.ID] = buildField(ASSIGNED_NODES, assignedNodes || []);
        domainFields[ADSYNC_NODES.ID] = buildField(ADSYNC_NODES, domainObj.ADSyncDomainNodes || []);
        domainFields[DOMAIN_SERVICE_ACCOUNT.ID] = buildField(DOMAIN_SERVICE_ACCOUNT, {
            user: domainObj.ServiceAccountUsername || '',
            pass: ''
        });
    }
    return domainFields;
};

const addDomain = (fields, wizard, systemMode) => {
    const newDomainId = uuidV1();
    return {
        ...fields,
        [newDomainId]: {
            id: newDomainId,
            name: 'New domain',
            fields: initDomainField({ id: newDomainId, isNew: true }, [], fields[LDAP_IP_OPTIONS.ID].value, wizard),
            validators: {
                filledForwarding: [KERBEROS_FORWARDING.ID, NTLM_FORWARDING.ID, LDAP_FORWARDING.ID, LDAPS_FORWARDING.ID],
                ldapServerUnique: [LDAP_IP.ID, BACKUP_LDAP_IP.ID],
                adsyncNodesRequiredAndUnique: systemMode === SYSTEM_MODES.ON_PREM,
                uniqueName: { fieldId: DOMAIN_NAME.ID, caseSensitive: true }
            }
        }
    };
};

const deleteDomain = (fields, domainId) => {
    const newFields = { ...fields };
    delete newFields[domainId];
    return newFields;
};

const updateDomainsFromFile = (fields, newDomains, LdapServerOptions, wizard, systemMode) => {
    const newFields = { ...fields };
    for (const newDomain of newDomains) {
        newDomain.LdapDomainName = newDomain.DomainName;
        const newDomainId = uuidV1();
        newFields[newDomainId] = {
            id: newDomainId,
            name: newDomain.DomainName,
            fields: initDomainField(
                { id: newDomainId, isNew: true, ...newDomain },
                [],
                fields[LDAP_IP_OPTIONS.ID].value.concat(LdapServerOptions),
                wizard
            ),
            validators: {
                filledForwarding: [KERBEROS_FORWARDING.ID, NTLM_FORWARDING.ID, LDAP_FORWARDING.ID, LDAPS_FORWARDING.ID],
                ldapServerUnique: [LDAP_IP.ID, BACKUP_LDAP_IP.ID],
                adsyncNodesRequiredAndUnique: systemMode === SYSTEM_MODES.ON_PREM,
                uniqueName: { fieldId: DOMAIN_NAME.ID, caseSensitive: true }
            }
        };
    }
    newFields[LDAP_IP_OPTIONS.ID] = buildField(
        LDAP_IP_OPTIONS,
        fields[LDAP_IP_OPTIONS.ID].value.concat(LdapServerOptions)
    );
    return newFields;
};
